import { useEffect, useState } from 'react';
import { en } from '../../locales/en';
import { useGcsStore } from '../../store/useGcsStore';
import { useSelectedGcsConfigsStore } from '../../store/useSelectedGcsConfigsStore';
import type { Version } from '../../types/gcs/version';
import { VersionRow } from '../general/VersionRow';
import Dialog from './Dialog';

interface AboutDialogProps {
  onClose: () => void;
}

interface GcsVersionResult {
  nameCode: string;
  response: Version | null;
}

function AboutDialog({ onClose }: AboutDialogProps) {
  const selectedConfigs = useSelectedGcsConfigsStore((state) => state.selectedConfigs);
  const fetchVersion = useGcsStore((state) => state.fetchVersion);
  const [gcsVersions, setGcsVersions] = useState<GcsVersionResult[]>([]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAllVersions = async () => {
      const results = await Promise.all(
        selectedConfigs.map(async (config) => {
          try {
            const response = await fetchVersion(config.id);
            return {
              nameCode: config.nameCode,
              response
            };
          } catch {
            return {
              nameCode: config.nameCode,
              response: null
            };
          }
        })
      );

      if (!isCancelled) {
        setGcsVersions(results);
      }
    };

    fetchAllVersions();

    return () => {
      isCancelled = true;
    };
  }, [selectedConfigs, fetchVersion]);

  return (
    <Dialog title={en.aboutDialog.title} onClose={onClose}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <VersionRow label={en.aboutDialog.appLabel} version={__APP_VERSION__} revision={__COMMIT_HASH__} />
        {gcsVersions.map((gcsVersion) => (
          <VersionRow
            key={gcsVersion.nameCode}
            label={gcsVersion.nameCode}
            version={gcsVersion.response?.releaseVersion}
            revision={gcsVersion.response?.backendVersion}
          />
        ))}
      </div>
    </Dialog>
  );
}

export default AboutDialog;
